/* eslint-disable react/no-unescaped-entities */
/* eslint-disable no-nested-ternary */
/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Stack,
  Card,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Container,
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  Avatar,
  Chip,
  TextField,
  Fade,
  Snackbar,
  Alert,
  Tooltip,
} from '@mui/material';
import { Check, ExpandMore, Close, Send, Public, Stars } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getPlanData } from '../service/planService';
import { contactSalesService } from '../../sections/pricing/pricingService';
import { ContactSalesSuccessModal } from '../../sections/pricing/PricingModals';

const ScrollAnimation = ({ children, sx, direction, delay, ...props }) => (
  <Box
    sx={{
      ...sx,
      animation: 'fadeInUp 0.6s ease-out forwards',
      opacity: 0,
      transform: 'translateY(20px)',
      animationDelay: `${delay || 0}ms`,
      '@keyframes fadeInUp': {
        to: {
          opacity: 1,
          transform: 'translateY(0)',
        },
      },
    }}
    {...props}
  >
    {children}
  </Box>
);

function ContactSalesModal({ open, onClose, plan, onSubmit, loading }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });

  useEffect(() => {
    if (open) setFormData({ name: '', email: '', phone: '', company: '', message: '' });
  }, [open]);

  if (!plan) return null;

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      TransitionComponent={Fade}
      PaperProps={{
        sx: {
          borderRadius: { xs: '14px', sm: '18px' }, // Scaled down
          overflow: 'hidden',
          border: `1.5px solid ${plan.accentColor}44`,
          m: { xs: 1.5, sm: 2.5 }, // Scaled down
          maxHeight: 'calc(100% - 24px)',
        },
      }}
      slotProps={{ backdrop: { sx: { backdropFilter: 'blur(8px)', bgcolor: 'rgba(0,0,0,0.45)' } } }}
    >
      <form onSubmit={handleSubmit}>
        <Box
          sx={{
            background: `linear-gradient(135deg, ${plan.accentColor}18 0%, ${plan.accentColor}06 100%)`,
            px: { xs: 1.5, sm: 2.5 },
            pt: { xs: 2, sm: 2.5 },
            pb: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography
                variant="subtitle1"
                fontWeight={800}
                sx={{ lineHeight: 1.2, fontSize: '0.95rem' }}
              >
                Contact Sales for {plan.name}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.5, fontSize: '0.72rem' }}
              >
                Tell us about your organization&apos;s needs and we&apos;ll craft a custom solution.
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={onClose}
              sx={{ color: 'text.secondary', mt: -0.5, mr: -1 }}
            >
              <Close fontSize="small" sx={{ fontSize: 18 }} />
            </IconButton>
          </Stack>
        </Box>

        <DialogContent
          sx={{ px: { xs: 1.5, sm: 2.5 }, py: { xs: 1.5, sm: 2.5 }, overflowY: 'auto' }}
        >
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                required
                fullWidth
                label="Full Name"
                name="name"
                size="small"
                value={formData.name}
                onChange={handleChange}
                InputProps={{ sx: { borderRadius: '8px', fontSize: '0.85rem' } }}
              />
              <TextField
                required
                fullWidth
                label="Work Email"
                name="email"
                type="email"
                size="small"
                value={formData.email}
                onChange={handleChange}
                InputProps={{ sx: { borderRadius: '8px', fontSize: '0.85rem' } }}
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                fullWidth
                label="Phone Number"
                name="phone"
                size="small"
                value={formData.phone}
                onChange={handleChange}
                InputProps={{ sx: { borderRadius: '8px', fontSize: '0.85rem' } }}
              />
              <TextField
                required
                fullWidth
                label="Company Name"
                name="company"
                size="small"
                value={formData.company}
                onChange={handleChange}
                InputProps={{ sx: { borderRadius: '8px', fontSize: '0.85rem' } }}
              />
            </Stack>
            <TextField
              required
              fullWidth
              label="Tell us about your requirements"
              name="message"
              multiline
              rows={4}
              size="small"
              value={formData.message}
              onChange={handleChange}
              InputProps={{ sx: { borderRadius: '8px', fontSize: '0.85rem' } }}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: { xs: 1.5, sm: 2.5 }, pb: { xs: 1.5, sm: 2.5 }, pt: 1, gap: 1 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '0.78rem', // Scaled down
              borderColor: 'divider',
              color: 'text.secondary',
              py: 0.75,
              '&:hover': { borderColor: 'text.secondary' },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            endIcon={!loading && <Send sx={{ fontSize: 14 }} />}
            sx={{
              borderRadius: '8px',
              fontWeight: 700,
              fontSize: '0.8rem', // Scaled down
              py: 0.75,
              px: 2.5,
              bgcolor: plan.accentColor,
              '&:hover': { bgcolor: `${plan.accentColor}dd` },
              boxShadow: `0 4px 14px ${plan.accentColor}44`,
              '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
            }}
          >
            {loading ? 'Submitting...' : 'Send Request'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

// =========================================================================
// MAIN PRICING GRID
// =========================================================================
export const PricingGrid = ({ currentPlanId }) => {
  const [currency, setCurrency] = useState('INR');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [selectedPlanDetails, setSelectedPlanDetails] = useState(null);

  // Modals state
  const [openSelectionModal, setOpenSelectionModal] = useState(false);
  const [openContactModal, setOpenContactModal] = useState(false);
  const [openContactSuccessModal, setOpenContactSuccessModal] = useState(false);
  const [submittedContactData, setSubmittedContactData] = useState(null);
  const [loading, setLoading] = useState(false);

  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });
  const navigate = useNavigate();

  const showToast = (message, severity = 'info') => {
    setToast({ open: true, message, severity });
  };

  const handleCloseModals = () => {
    setOpenSelectionModal(false);
    setOpenContactModal(false);
    setSelectedPlanDetails(null);
  };
  const handleContactSubmit = async (formData) => {
    setLoading(true);
    try {
      const payload = {
        ...(formData || {}),
        planName: selectedPlanDetails?.name || 'Enterprise',
      };
      await contactSalesService(payload);
      setSubmittedContactData(formData);
      setOpenContactModal(false);
      setSelectedPlanDetails(null);
      setOpenContactSuccessModal(true);
    } catch (error) {
      console.error('Contact sales error:', error);
      showToast('Unable to submit inquiry. Please try again or email sales@cybermeru.com directly.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = (plan) => {
    const isCustom =
      currency === 'INR' ? plan.priceMonthlyINR === 'Custom' : plan.priceMonthlyUSD === 'Custom';
    const isAnnual = billingCycle === 'annual';

    if (isCustom) {
      setSelectedPlanDetails(plan);
      setOpenContactModal(true);
      return;
    }

    const selectedPlanId = isAnnual ? plan.idAnnual : plan.idMonthly;
    const selectedPrice = isAnnual
      ? currency === 'INR'
        ? plan.priceAnnualINR
        : plan.priceAnnualUSD
      : currency === 'INR'
      ? plan.priceMonthlyINR
      : plan.priceMonthlyUSD;

    navigate('/onboard', {
      state: {
        selectedPlan: {
          id: plan.id,
          name: plan.name,
          desc: plan.desc,
          exactPlanId: selectedPlanId,
          finalPrice: selectedPrice,
          includedFeatures: plan.includedFeatures,
          excludedFeatures: plan.excludedFeatures,
          priceMonthlyINR: plan.priceMonthlyINR,
          priceMonthlyUSD: plan.priceMonthlyUSD,
          perUserINR: plan.perUserINR,
          perUserUSD: plan.perUserUSD,
          currency,
          currencySymbol: currency === 'INR' ? '₹' : '$',
          isAnnual: false,
          isTrial: plan.id === 'free',
        },
      },
    });

    setSelectedPlanDetails(plan);
    setOpenSelectionModal(true);
  };

  return (
    <Box>
      <ScrollAnimation direction="up">
        <Box textAlign="center" mb={2} px={1.5}>
          {/* <Box display="flex" justifyContent="center" mb={1}>
            <Chip
              label="Introductory Pricing"
              size="small"
              color="success"
              sx={{
                fontWeight: 600,
                fontSize: '0.65rem', // Scaled down
                letterSpacing: 0.5,
                px: 0.25,
                height: 22,
              }}
            />
          </Box> */}
          <Typography
            variant="h4"
            color="text.primary"
            mb={0.5}
            sx={{
              fontSize: { xs: '1.35rem', md: '1.55rem' }, // Scaled down
              letterSpacing: '-0.5px',
              fontWeight: 800,
            }}
          >
            Simple, transparent pricing
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            fontWeight={500}
            sx={{ maxWidth: 580, mx: 'auto', display: 'block', mb: 1.5, fontSize: '0.82rem' }}
          >
            No hidden fees. Choose the perfect plan for your team's size and hiring velocity.
          </Typography>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems="center"
            justifyContent="center"
            spacing={{ xs: 1.5, sm: 3 }}
            mb={2}
          >
            {/* Currency Toggle */}
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                bgcolor: 'background.default',
                border: '1.5px solid',
                borderColor: 'divider',
                borderRadius: '10px',
                p: '2px',
                gap: '2px',
              }}
            >
              {[
                { key: 'INR', flag: 'https://flagcdn.com/w20/in.png', label: 'India (₹)' },
                { key: 'USD', flag: null, label: 'Global ($)', disabled: true, tooltip: 'Coming Soon' },
              ].map((opt) => {
                const btn = (
                  <Box
                    onClick={() => !opt.disabled && setCurrency(opt.key)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      px: 1.25,
                      py: 0.5,
                      borderRadius: '8px',
                      cursor: opt.disabled ? 'not-allowed' : 'pointer',
                      opacity: opt.disabled ? 0.45 : 1,
                      fontWeight: 700,
                      fontSize: '0.75rem', // Scaled down
                      transition: 'all 0.18s ease',
                      bgcolor: currency === opt.key && !opt.disabled ? 'background.paper' : 'transparent',
                      color: opt.disabled ? 'text.disabled' : currency === opt.key ? 'text.primary' : 'text.disabled',
                      boxShadow: currency === opt.key && !opt.disabled ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
                      userSelect: 'none',
                    }}
                  >
                    {opt.flag ? (
                      <Box
                        component="img"
                        src={opt.flag}
                        alt={opt.key}
                        sx={{ width: 14, borderRadius: '2px', flexShrink: 0 }}
                      />
                    ) : (
                      <Public sx={{ fontSize: 13, flexShrink: 0 }} />
                    )}
                    {opt.label}
                  </Box>
                );

                return opt.tooltip ? (
                  <Tooltip key={opt.key} title={opt.tooltip} arrow placement="top">
                    <span style={{ display: 'inline-flex' }}>{btn}</span>
                  </Tooltip>
                ) : (
                  <React.Fragment key={opt.key}>{btn}</React.Fragment>
                );
              })}
            </Box>

            {/* Billing Cycle Toggle */}
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                bgcolor: 'background.default',
                border: '1.5px solid',
                borderColor: 'divider',
                borderRadius: '10px',
                p: '2px',
                gap: '2px',
              }}
            >
              {[
                { key: 'monthly', label: 'Monthly', badge: null },
                { key: 'annual', label: 'Annual', badge: 'Save Upto 20%' },
              ].map((opt) => (
                <Box
                  key={opt.key}
                  onClick={() => setBillingCycle(opt.key)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1.25,
                    py: 0.5,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 700,
                    fontSize: '0.75rem', // Scaled down
                    transition: 'all 0.18s ease',
                    bgcolor: billingCycle === opt.key ? 'background.paper' : 'transparent',
                    color: billingCycle === opt.key ? 'text.primary' : 'text.disabled',
                    boxShadow: billingCycle === opt.key ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
                    userSelect: 'none',
                  }}
                >
                  {opt.label}
                  {opt.badge && (
                    <Box
                      component="span"
                      sx={{
                        fontSize: '0.55rem', // Scaled down
                        fontWeight: 800,
                        bgcolor: '#fef3c7',
                        color: '#b45309',
                        border: '1px solid #fde68a',
                        px: 0.5,
                        py: 0.1,
                        borderRadius: '4px',
                        letterSpacing: 0.2,
                      }}
                    >
                      {opt.badge}
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          </Stack>
        </Box>
      </ScrollAnimation>

      {/* ── COMPACT MODERN PRICING BANNER ── */}
      <ScrollAnimation direction="up" delay={50}>
        <Box display="flex" justifyContent="center" mt={1} mb={2.5}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 1.75,
              py: 0.6,
              borderRadius: '999px',
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 2px 10px rgba(0,0,0,0.03), inset 0 1px 0 rgba(255,255,255,0.4)',
              position: 'relative',
              overflow: 'hidden',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 14px rgba(99, 102, 241, 0.08)',
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '50%',
                height: '100%',
                background:
                  'linear-gradient(90deg, transparent, rgba(99, 102, 241, 0.08), transparent)',
                transform: 'skewX(-20deg)',
                animation: 'shimmerSweep 4s infinite',
              },
              '@keyframes shimmerSweep': {
                '0%': { left: '-100%' },
                '50%': { left: '200%' },
                '100%': { left: '200%' },
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 20,
                height: 20,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
                color: 'white',
                flexShrink: 0,
              }}
            >
              <Stars sx={{ fontSize: '0.8rem' }} />
            </Box>

            <Typography
              sx={{
                fontSize: { xs: '0.68rem', sm: '0.75rem' },
                fontWeight: 600,
                color: 'text.secondary',
                letterSpacing: '0.01em',
              }}
            >
              <Box
                component="span"
                sx={{
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Limited Time:
              </Box>{' '}
              Introductory pricing applied to all premium plans
            </Typography>
          </Box>
        </Box>
      </ScrollAnimation>
      {/* ────────────────────────────────────── */}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' },
          gap: { xs: 1.5, lg: 1.5 }, // Scaled down
          maxWidth: 1000, // Scaled down
          mx: 'auto',
          mt: 2.5, // Scaled down
        }}
      >
        {getPlanData().map((plan, index) => {
          const isAnnual = billingCycle === 'annual';
          const isCurrentPlan = currentPlanId === plan.id;

          const basePrice =
            currency === 'INR' ? Number(plan.basePriceINR || 0) : Number(plan.basePriceUSD || 0);
          const perUserPrice =
            currency === 'INR' ? Number(plan.perUserINR || 0) : Number(plan.perUserUSD || 0);
          const activePrice = isAnnual ? Number((basePrice * 0.8).toFixed(1)) : basePrice;
          const activePerUser = perUserPrice;
          const originalPrice = isAnnual ? basePrice * 12 : basePrice;
          const originalPerUser = perUserPrice;
          const currencySymbol = currency === 'INR' ? '₹' : '$';

          const isFree = plan.id === 'free';
          const isEnterprise = plan.id === 'enterprise';
          const displayPrice = isFree ? 'Free' : isEnterprise ? 'Custom' : activePrice;

          const allFeatures = [
            ...(plan.includedFeatures || []).map((f) => ({ text: f, included: true })),
            ...(plan.excludedFeatures || []).map((f) => ({ text: f, included: false })),
          ];

          return (
            <Box key={plan.id} sx={{ display: 'flex', width: '100%' }}>
              <ScrollAnimation
                direction="up"
                delay={index * 100}
                sx={{ display: 'flex', width: '100%', height: '100%' }}
              >
                <Card
                  elevation={plan.highlight ? 6 : 0}
                  sx={{
                    height: '100%',
                    p: 1.75, // Scaled down
                    position: 'relative',
                    border: plan.highlight ? 2 : 1,
                    borderColor: plan.highlight ? plan.accentColor : 'divider',
                    bgcolor: 'background.paper',
                    transform: { lg: plan.highlight ? 'scale(1.025)' : 'none' }, // Scaled down slightly
                    zIndex: plan.highlight ? 10 : 1,
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: '14px', // Scaled down
                    overflow: 'visible',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 10px 20px rgba(0,0,0,0.06)',
                      transform: { lg: plan.highlight ? 'scale(1.035)' : 'scale(1.015)' },
                    },
                  }}
                >
                  {plan.highlight && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -8, // Scaled down
                        left: '50%',
                        transform: 'translateX(-50%)',
                        bgcolor: plan.accentColor,
                        color: '#fff',
                        px: 1.25,
                        py: 0.2,
                        borderRadius: '10px',
                        fontSize: '0.55rem', // Scaled down
                        fontWeight: 800,
                        letterSpacing: 0.5,
                        boxShadow: `0 3px 10px ${plan.accentColor}44`,
                      }}
                    >
                      MOST POPULAR
                    </Box>
                  )}

                  <Stack alignItems="center" spacing={0.25} mb={0.5} mt={plan.highlight ? 0.5 : 0}>
                    <Avatar
                      sx={{
                        width: 28,
                        height: 28,
                        bgcolor: `${plan.accentColor}15`,
                        color: plan.accentColor,
                      }}
                    >
                      {React.isValidElement(plan.icon) ? (
                        plan.icon
                      ) : (
                        <Check sx={{ fontSize: 16 }} />
                      )}
                    </Avatar>
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      color="text.primary"
                      sx={{ fontSize: '0.9rem' }}
                    >
                      {plan.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.7rem', textAlign: 'center', minHeight: 18 }}
                    >
                      {plan.desc}
                    </Typography>
                  </Stack>

                  <Box
                    mb={0.5}
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                  >
                    {isEnterprise ? (
                      <Typography
                        sx={{
                          fontSize: '1.75rem',
                          fontWeight: 700,
                          color: 'text.primary',
                          letterSpacing: '-0.03em',
                        }}
                      >
                        Custom
                      </Typography>
                    ) : isFree ? (
                      <Typography
                        sx={{
                          fontSize: '1.75rem',
                          fontWeight: 700,
                          color: 'text.primary',
                          letterSpacing: '-0.03em',
                        }}
                      >
                        Free
                      </Typography>
                    ) : (
                      <>
                        <Box display="flex" alignItems="baseline" flexWrap="wrap">
                          {isAnnual &&
                            originalPrice !== 'Custom' &&
                            originalPrice !== '0' &&
                            originalPrice !== activePrice && (
                              <Typography
                                variant="body2"
                                color="text.disabled"
                                sx={{
                                  textDecoration: 'line-through',
                                  fontWeight: 600,
                                  mr: 1,
                                  opacity: 0.7,
                                  fontSize: '0.8rem',
                                }}
                              >
                                {currencySymbol}
                                {originalPrice}
                              </Typography>
                            )}
                          <Box
                            display="flex"
                            alignItems="baseline"
                            justifyContent="center"
                            gap={0.5}
                          >
                            {activePrice !== 'Custom' && activePrice !== '0' && (
                              <Typography
                                variant="subtitle1"
                                fontWeight={800}
                                color={isAnnual ? 'success.main' : 'text.primary'}
                                mr={0.15}
                              >
                                {currencySymbol}
                              </Typography>
                            )}
                            <Typography
                              sx={{
                                fontSize: '1.75rem', // Scaled down
                                fontWeight: 700,
                                color:
                                  displayPrice === 'Custom'
                                    ? 'text.primary'
                                    : isAnnual && activePrice !== 'Custom' && activePrice !== '0'
                                    ? 'success.main'
                                    : 'text.primary',
                                lineHeight: 1,
                              }}
                            >
                              {Number(displayPrice).toLocaleString()}
                            </Typography>
                            {activePrice !== 'Custom' && activePrice !== 'Free' && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                ml={0.25}
                                sx={{ fontSize: '0.7rem' }}
                              >
                                / month
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        {activePerUser > 0 && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0, fontSize: '0.65rem', fontWeight: 600 }}
                          >
                            +{' '}
                            {isAnnual && originalPerUser !== activePerUser && (
                              <Box
                                component="span"
                                sx={{ textDecoration: 'line-through', opacity: 0.6, mr: 0.25 }}
                              >
                                {currencySymbol}
                                {originalPerUser}
                              </Box>
                            )}
                            <Box
                              component="span"
                              sx={{
                                color: isAnnual ? 'success.main' : 'inherit',
                                fontWeight: isAnnual ? 700 : 600,
                              }}
                            >
                              {currencySymbol}
                              {activePerUser}
                            </Box>{' '}
                            / user / month
                          </Typography>
                        )}
                      </>
                    )}
                  </Box>

                  <Button
                    fullWidth
                    disabled={isCurrentPlan}
                    variant={plan.highlight && !isCurrentPlan ? 'contained' : 'outlined'}
                    color="primary"
                    sx={{
                      mb: 1.25,
                      py: 0.6, // Scaled down
                      borderRadius: '8px',
                      fontWeight: 700,
                      fontSize: '0.72rem', // Scaled down
                      ...(isCurrentPlan
                        ? {
                            borderColor: 'divider',
                            color: 'text.primary',
                            bgcolor: 'background.default',
                            '&:hover': {
                              borderColor: plan.accentColor,
                              bgcolor: `${plan.accentColor}08`,
                            },
                          }
                        : plan.highlight
                        ? {
                            bgcolor: plan.accentColor,
                            boxShadow: `0 3px 10px ${plan.accentColor}44`,
                            '&:hover': { bgcolor: `${plan.accentColor}dd` },
                          }
                        : {
                            borderColor: 'divider',
                            color: 'text.primary',
                            bgcolor: 'background.default',
                            '&:hover': {
                              borderColor: plan.accentColor,
                              bgcolor: `${plan.accentColor}08`,
                            },
                          }),
                    }}
                    onClick={() => handleSelectPlan(plan)}
                  >
                    {plan.id === 'enterprise' ? 'Contact Sales' : 'Start for Free'}
                  </Button>

                  <Box sx={{ flexGrow: 1 }}>
                    <Stack spacing={0.6}>
                      {allFeatures.slice(0, 5).map((feature, i) => (
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="flex-start"
                          key={feature.text || i}
                        >
                          <Box
                            sx={{
                              width: 12, // Scaled down
                              height: 12, // Scaled down
                              borderRadius: '50%',
                              bgcolor: feature.included ? `${plan.accentColor}15` : 'action.hover',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                              mt: 0.25,
                            }}
                          >
                            {feature.included ? (
                              <Check sx={{ fontSize: 8, color: plan.accentColor }} />
                            ) : (
                              <Close sx={{ fontSize: 8, color: 'error.light' }} />
                            )}
                          </Box>
                          <Typography
                            variant="caption"
                            color={feature.included ? 'text.primary' : 'text.secondary'}
                            fontWeight={feature.included ? 600 : 400}
                            sx={{ fontSize: '0.65rem', lineHeight: 1.2 }}
                          >
                            {feature.text}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Box>

                  {allFeatures.length > 5 && (
                    <Button
                      fullWidth
                      variant="text"
                      onClick={() => navigate('/pricing-comparison')}
                      sx={{
                        mt: 1.25,
                        py: 0.4,
                        fontSize: '0.65rem', // Scaled down
                        fontWeight: 700,
                        textTransform: 'none',
                        color: plan.accentColor,
                        bgcolor: `${plan.accentColor}08`,
                        borderRadius: '6px',
                        '&:hover': { bgcolor: `${plan.accentColor}15` },
                      }}
                    >
                      View all features
                    </Button>
                  )}
                </Card>
              </ScrollAnimation>
            </Box>
          );
        })}
      </Box>

      <ScrollAnimation direction="up" delay={200}>
        <Box textAlign="center" mt={4} px={2} maxWidth={780} mx="auto">
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: '0.7rem', lineHeight: 1.5 }}
          >
            * Prices shown reflect our limited-time <strong>introductory offer</strong> and may be
            subject to change in the future.
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: '0.7rem', lineHeight: 1.5, mt: 0.5 }}
          >
            ** Local taxes, VAT, GST, or other regional regulatory duties are not included in the
            listed base prices and will be calculated and applied dynamically at checkout based on
            your organization's billing country and local laws.
          </Typography>
        </Box>
      </ScrollAnimation>

      <ContactSalesModal
        open={openContactModal}
        onClose={handleCloseModals}
        plan={selectedPlanDetails}
        onSubmit={handleContactSubmit}
        loading={loading}
      />

      <ContactSalesSuccessModal
        open={openContactSuccessModal}
        onClose={() => setOpenContactSuccessModal(false)}
        contactData={submittedContactData}
      />
    </Box>
  );
};

export const FAQ = () => {
  const faqs = [
    {
      q: 'How long does implementation take?',
      a: 'Unlike legacy ATS platforms that take months, MeruDesk can be fully deployed and integrated with your existing HRIS in under 14 days thanks to our pre-built integrations.',
    },
    {
      q: 'Does MeruBot conduct actual interviews?',
      a: 'Yes! MeruBot can conduct technical or behavioral screening calls using voice AI, completely eliminating early-stage screening fatigue for your recruiters.',
    },
    {
      q: 'Can we migrate our data from our old ATS?',
      a: 'Yes, we provide seamless data migration from your existing ATS with full support. Migration is included at no extra cost for Premium plans, and can also be arranged for Custom plans based on your needs.',
    },
    {
      q: 'Is MeruDesk compliant with GDPR and ISO?',
      a: 'MeruDesk is designed and developed with application security requirements in mind by an ISO 27001:2022 certified organization, CyberMeru Technologies. It is also built considering GDPR requirements.',
    },
  ];

  return (
    <Box sx={{ py: { xs: 4, md: 7 } }}>
      <Container maxWidth="md">
        <ScrollAnimation direction="up">
          <Typography
            variant="h4"
            color="text.primary"
            mb={3}
            textAlign="center"
            sx={{
              fontSize: { xs: '1.4rem', md: '1.9rem' }, // Scaled down
              letterSpacing: '-0.5px',
              fontWeight: 800,
            }}
          >
            Frequently Asked Questions
          </Typography>
          <Box>
            {faqs.map((faq, i) => (
              <Accordion
                key={faq.q}
                elevation={0}
                sx={{
                  mb: 1.25,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: '10px !important',
                  bgcolor: 'background.paper',
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': {
                    m: '0 0 10px 0',
                    borderColor: '#3b82f6',
                    boxShadow: '0 3px 10px rgba(59, 130, 246, 0.06)',
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore sx={{ fontSize: 18, color: 'text.secondary' }} />}
                  sx={{ px: 2.5, minHeight: '50px !important' }} // Scaled down
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    color="text.primary"
                    sx={{ fontSize: '0.9rem' }} // Scaled down
                  >
                    {faq.q}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    lineHeight={1.5}
                    sx={{ fontSize: '0.8rem' }} // Scaled down
                  >
                    {faq.a}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </ScrollAnimation>
      </Container>
    </Box>
  );
};
